import { createSelector } from "@reduxjs/toolkit";
import { CustomerAccountRole, RewardsAccount, RewardsPrice } from "redmond";
import { isCorpTenant } from "@capone/common";
import { IStoreState } from "../../../../reducers/types";
import { ICorpCarRewardsState } from "../index";
import { determineHertzTierStatus, type HertzTierStatus } from "./hertzTierStatus";
import { config } from "../../../../api/config";

export const getIsFirstLaunch = (state: IStoreState) =>
  state.carRewards.isFirstLaunch;

export const getAgentEmail = (state: IStoreState) =>
  state.carRewards.agentEmail;

export const getRewardsAccounts = (state: IStoreState) =>
  state.carRewards.rewardsAccounts;

export const getIsVentureX = (state: IStoreState) =>
  getRewardsAccounts(state).some((acc) =>
    acc.productDisplayName.includes("Venture X")
  );

export const getUserSelectedAccountReferenceId = (state: IStoreState) =>
  state.carRewards.selectedAccountReferenceId;

export const getFetchRewardsAccountsCallState = (state: IStoreState) =>
  state.carRewards.fetchRewardsAccountsCallState;

export const getUserInfo = (state: IStoreState) => state.carRewards.userInfo;

export const getCorporateInfo = (state: IStoreState) =>
  state.carRewards.corporateInfo;

export const getRewardsAccountWithLargestValue = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount | undefined => rewardsAccounts[0]
);

// the rewards account selection doesn't have a default state where none of the available options is selected
export const getDefaultSelectedAccountReferenceId = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string | null => {
    if (rewardsAccounts.length > 0) {
      return rewardsAccounts[0].accountReferenceId;
    }

    return null;
  }
);

// this is the primary selector used outside the component
// either returns the user selected account id, or the default from above if not present
export const getSelectedAccountReferenceId = createSelector(
  getUserSelectedAccountReferenceId,
  getDefaultSelectedAccountReferenceId,
  (
    userSelectedAccountReferenceId,
    defaultSelectedAccountReferenceId
  ): string | null =>
    userSelectedAccountReferenceId ?? defaultSelectedAccountReferenceId
);

export const getSelectedAccountReferenceIdIfRedemptionEnabled = createSelector(
  getSelectedAccountReferenceId,
  getRewardsAccounts,
  (accountReferenceId, rewardsAccounts) => {
    const account = rewardsAccounts.find(
      (acc) => acc.accountReferenceId === accountReferenceId
    );

    return account?.allowRewardsRedemption ?? true ? accountReferenceId : null;
  }
);

export const getSelectedAccount = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (rewardsAccounts, referenceId): RewardsAccount | undefined =>
    rewardsAccounts.find(
      (account) => account.accountReferenceId === referenceId
    )
);

export const getSelectedAccountIndex = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, id) =>
    accounts.findIndex((account) => account.accountReferenceId === id)
);

export const getRewardsAccountWithLargestEarnForBanner = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount => rewardsAccounts[0]
);

export const getRewardsAccountForBanner = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  (highestEarnAccount): RewardsAccount | undefined => highestEarnAccount
);

export const getCardPaymentRewardsAccount = (state: IStoreState) => {
  const accountId = state.carBook.paymentMethodRewardsAccountId;
  const rewardsAccounts = getRewardsAccounts(state);
  return rewardsAccounts.find(
    (account) => account.accountReferenceId === accountId
  );
};

export const getEarnForHighestEarnRewardsAccount = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  (state: IStoreState) => state.carBook.productEarnValue,
  (state: IStoreState) => state.carBook.priceQuote,
  (state: IStoreState) => state.carBook.rewardsPaymentInFiatCurrency,
  (state: IStoreState) => state.carBook.offerToApply,
  (state: IStoreState) => state.carBook.creditToApply,
  (state: IStoreState) => state.carBook.paymentMethodRewardsAccountId,
  (
    highestEarnRewardsAccount,
    productEarnValue,
    priceQuote,
    rewardsPaymentInFiatCurrency,
    offerToApply,
    creditToApply,
    paymentMethodRewardsAccountId
  ): RewardsPrice | undefined => {
    const rewardsAccount = highestEarnRewardsAccount;

    if (!rewardsAccount) {
      return undefined;
    }

    if (rewardsAccount?.customerAccountRole !== CustomerAccountRole.Primary) {
      return undefined;
    }

    const tripTotal = priceQuote?.vehAvail?.rentalRate?.totalCharge?.fiat.value;

    if (!tripTotal) {
      if (!productEarnValue) {
        return undefined;
      }

      return {
        value: productEarnValue.amount,
        currency:
          productEarnValue.correlatedCurrency ||
          rewardsAccount.rewardsBalance.currency,
        currencyDescription:
          productEarnValue.correlatedCurrency ||
          rewardsAccount.rewardsBalance.currencyDescription ||
          "miles",
      };
    }

    if (paymentMethodRewardsAccountId !== rewardsAccount.accountReferenceId) {
      return undefined;
    }
    const rewardsPayment = rewardsPaymentInFiatCurrency?.value || 0;
    const offerAmount = offerToApply?.amount?.amount || 0;
    const creditAmount = creditToApply?.amount?.amount || 0;

    const totalCreditsAndRewards = rewardsPayment + offerAmount + creditAmount;
    const creditCardPaymentRequired = Math.max(
      0,
      tripTotal - totalCreditsAndRewards
    );

    if (creditCardPaymentRequired <= 0) {
      return undefined;
    }

    if (!productEarnValue) {
      return undefined;
    }

    return {
      value: productEarnValue.amount,
      currency:
        productEarnValue.correlatedCurrency ||
        rewardsAccount.rewardsBalance.currency,
      currencyDescription:
        productEarnValue.correlatedCurrency ||
        rewardsAccount.rewardsBalance.currencyDescription ||
        "miles",
    };
  }
);

export const getRewardsAccountsReferenceIds = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string[] =>
    rewardsAccounts.map((account) => account.accountReferenceId)
);

// Corporate Travel
export const getIsInPolicy = (state: IStoreState) => {
  const { selectedVehicle } = state.carShop;
  return selectedVehicle && "corporateTravel" in selectedVehicle
    ? selectedVehicle.corporateTravel?.policyCompliance.isInPolicy
    : true;
};

export const getIsAutoApprovalEnabled = (state: IStoreState) =>
  (state.carRewards as ICorpCarRewardsState).isAutoApprovalEnabled;

export const getAllowRewardsRedemptionInAnyAccount = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): boolean =>
    rewardsAccounts.some((account) => account.allowRewardsRedemption ?? true)
);

export const getAllowRewardsWithPolicy = createSelector(
  getAllowRewardsRedemptionInAnyAccount,
  (allowRewardsAccounts): boolean => {
    if (isCorpTenant(config.TENANT)) {
      return allowRewardsAccounts;
    }
    return true;
  }
);

export const getUserIsPrimaryCardHolder = createSelector(
  getRewardsAccounts,
  (rewardsAccounts) =>
    rewardsAccounts.some((account) => account.customerAccountRole === "Primary")
);

export const getHertzTierStatus = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): HertzTierStatus => determineHertzTierStatus(rewardsAccounts)
);
